import { variableIsAnImage } from '../RightSide/variablesUtils';
import { buildRecursiveFieldName } from '../../utils/recursiveSections';

/**
 * Liste plate des variables images disponibles pour le document courant.
 *
 * Reprend la même source et les mêmes règles que `ImageCascader` (panneau de
 * droite), mais renvoie directement le couple `src` / `adminValue` à poser dans
 * `box.content`, afin de pouvoir insérer une image variable sans passer par le
 * cascader (ex : menu contextuel "/" de l'éditeur de texte).
 */

const stripApiMeta = (variablesApi) => {
  const { variable_groups, custom_text_variables, ...data } = variablesApi;
  return data;
};

const buildOption = ({ key, name, adminValue, rawValue, isRecursive = false }) => {
  const value = rawValue?.toString();
  return {
    key,
    name,
    adminValue,
    src: value && value !== '' ? value : `#{${adminValue}}`,
    isRecursive,
  };
};

const templateOptions = (data, t) => {
  const groups =
    typeof data['offer_variables'] === 'undefined' ? data : data['offer_variables'];
  if (!groups) return [];
  return Object.keys(groups)
    .map((group) =>
      Object.values(groups[group])
        .filter((variable) => variableIsAnImage(variable))
        .map((name) =>
          buildOption({
            key: `${group}.${name}-variable`,
            name: t(`${group}.${name}`),
            adminValue: `${group}.${name}`,
            // Dans un modèle, aucune valeur n'est résolue : on pose le token.
            rawValue: null,
          })
        )
    )
    .flat();
};

const recursiveOptions = (recursiveModelName, recursiveModelFields, t) => {
  const modelFields = recursiveModelFields?.[recursiveModelName] ?? [];
  return modelFields
    .filter((field) => field.type === 'image')
    .map((field) =>
      buildOption({
        key: `${recursiveModelName}.${field.key}-recursive`,
        name: buildRecursiveFieldName(recursiveModelName, field.label, t),
        adminValue: `${recursiveModelName}.${field.key}`,
        rawValue: null,
        isRecursive: true,
      })
    );
};

const opportunityDocumentOptions = (data, t) =>
  Object.keys(data)
    .map((group) =>
      Object.keys(data[group])
        .filter((variable) => variableIsAnImage(variable))
        .map((name) =>
          buildOption({
            key: `${group}.${name}`,
            name: t(`${group}.${name}`),
            adminValue: `${group}.${name}`,
            rawValue: data[group][name],
          })
        )
    )
    .flat();

const offerDocumentOptions = (data, t) => {
  const offerVariables = data.offer_variables ?? {};
  const offerOptions = Object.keys(offerVariables)
    .map((group) =>
      Object.keys(offerVariables[group])
        .filter((variable) => variableIsAnImage(variable))
        .map((name) =>
          buildOption({
            key: `offer.${group}.${name}`,
            name: `${t('offer')} / ${t(`${group}.${name}`)}`,
            adminValue: `${group}.${name}`,
            rawValue: offerVariables[group][name],
          })
        )
    )
    .flat();

  const opportunitiesVariables = Array.isArray(data.opportunities_variables)
    ? data.opportunities_variables
    : [];
  const opportunityOptions = opportunitiesVariables
    .map((opportunity, idx) => {
      const opportunityName = Object.keys(opportunity).toString();
      const opportunityData = opportunity[opportunityName];
      if (!opportunityData) return [];
      return Object.keys(opportunityData)
        .map((group) =>
          Object.keys(opportunityData[group])
            .filter((variable) => variableIsAnImage(variable))
            .map((name) =>
              buildOption({
                key: `${idx}.${group}.${name}`,
                name: `${opportunityName} / ${t(`${group}.${name}`)}`,
                // Les variables d'opportunité sont préfixées par l'index de
                // l'opportunité dans l'offre (cf. ImageCascader).
                adminValue: `${idx}.${group}.${name}`,
                rawValue: opportunityData[group][name],
              })
            )
        )
        .flat();
    })
    .flat();

  return [...offerOptions, ...opportunityOptions];
};

export const buildImageVariableOptions = ({
  defaultVariablesApi,
  currentDocument,
  recursiveModelName,
  recursiveModelFields,
  t,
}) => {
  if (!defaultVariablesApi || !currentDocument) return [];

  const data = stripApiMeta(defaultVariablesApi);

  let options;
  if (currentDocument.type === 'opportunity') {
    options = opportunityDocumentOptions(data, t);
  } else if (currentDocument.type === 'template') {
    options = templateOptions(data, t);
    if (recursiveModelName) {
      options = [
        ...recursiveOptions(recursiveModelName, recursiveModelFields, t),
        ...options,
      ];
    }
  } else {
    options = offerDocumentOptions(data, t);
  }

  return options.sort((a, b) => {
    if (a.isRecursive !== b.isRecursive) return a.isRecursive ? -1 : 1;
    return (a.name ?? '').localeCompare(b.name ?? '');
  });
};

export default buildImageVariableOptions;
